/**
 * Client-side kickoff for the two org-scoped Google marketing integrations
 * added alongside the Marketing Dashboard (migration 101):
 *
 *   • search_console  Google Search Console — powers the dashboard's Search tab.
 *   • youtube         YouTube channel analytics — powers the Social tab's
 *                     YouTube section.
 *
 * Both reuse the GA4 credential model rather than the BYO-app-credentials
 * model that `marketingOAuth.ts` implements for the six ad/social platforms:
 * one Google Cloud OAuth client (`VITE_GOOGLE_CLIENT_ID`, matching the
 * `GOOGLE_CLIENT_ID` server secret) serves GA4, Search Console, and YouTube,
 * so connecting a second Google product needs no extra credential setup.
 *
 * Each product still gets its own authorization round-trip and its own
 * refresh token. Bundling all three scopes into one consent screen would mean
 * revoking YouTube access also silently breaks GA4 reporting.
 *
 * See `ga4OAuth.ts` for why `access_type=offline` + `prompt=consent` are
 * mandatory here — without both, Google omits the refresh token for a user
 * who has previously approved this client, which breaks the hourly cron sync
 * in a way that isn't visible until the access token expires an hour later.
 */

export type GoogleMarketingProvider = 'search_console' | 'youtube';

export interface GoogleMarketingProviderConfig {
  id: GoogleMarketingProvider;
  name: string;
  /** URL path Google redirects back to; must be registered in Cloud Console. */
  callbackPath: string;
  scopes: readonly string[];
  stateStorageKey: string;
  /** Edge Function that exchanges the code and lists available resources. */
  callbackFunction: string;
  /** Edge Function that persists the operator's resource choice. */
  setResourceFunction: string;
  /** Edge Function that pulls metrics once the resource is chosen. */
  syncFunction: string;
  /** What the operator is picking on the callback screen. */
  resourceLabel: string;
}

const PROVIDERS: Record<GoogleMarketingProvider, GoogleMarketingProviderConfig> = {
  search_console: {
    id: 'search_console',
    name: 'Google Search Console',
    callbackPath: '/integrations/search-console/callback',
    // `webmasters.readonly` covers both sites.list (the property picker) and
    // searchanalytics.query (the sync). There is no narrower read scope.
    scopes: ['https://www.googleapis.com/auth/webmasters.readonly'],
    stateStorageKey: 'search_console_oauth_state',
    callbackFunction: 'search-console-oauth-callback',
    setResourceFunction: 'search-console-set-site',
    syncFunction: 'search-console-sync',
    resourceLabel: 'Search Console property',
  },
  youtube: {
    id: 'youtube',
    name: 'YouTube',
    callbackPath: '/integrations/youtube/callback',
    scopes: [
      // Channel identity, subscriber counts, video titles.
      'https://www.googleapis.com/auth/youtube.readonly',
      // The day-by-day time series lives in the separate YouTube Analytics
      // API, which has its own scope — `youtube.readonly` alone returns only
      // lifetime totals, which can't be charted.
      'https://www.googleapis.com/auth/yt-analytics.readonly',
    ],
    stateStorageKey: 'youtube_oauth_state',
    callbackFunction: 'youtube-oauth-callback',
    setResourceFunction: 'youtube-set-channel',
    syncFunction: 'youtube-sync',
    resourceLabel: 'YouTube channel',
  },
};

export const GOOGLE_MARKETING_PROVIDERS: GoogleMarketingProviderConfig[] = [
  PROVIDERS.search_console,
  PROVIDERS.youtube,
];

export function getGoogleMarketingProvider(
  id: GoogleMarketingProvider,
): GoogleMarketingProviderConfig {
  return PROVIDERS[id];
}

/**
 * Redirect URI Google sends the operator back to. Must match byte-for-byte
 * between the authorize request, the token exchange, and the value registered
 * under "Authorized redirect URIs" in the Google Cloud Console.
 */
export function googleMarketingRedirectUri(id: GoogleMarketingProvider): string {
  return `${window.location.origin}${PROVIDERS[id].callbackPath}`;
}

/**
 * Starts the OAuth flow. Returns null on success (the tab is navigating away)
 * or a human-readable error string when the environment isn't configured, so
 * the caller can surface it as a toast.
 */
export function startGoogleMarketingOAuth(id: GoogleMarketingProvider): string | null {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) {
    return 'VITE_GOOGLE_CLIENT_ID is not set. Add it to .env (it must match the GOOGLE_CLIENT_ID server secret) and redeploy.';
  }

  const config = PROVIDERS[id];
  const state = crypto.randomUUID();
  sessionStorage.setItem(config.stateStorageKey, state);

  const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', config.scopes.join(' '));
  url.searchParams.set('redirect_uri', googleMarketingRedirectUri(id));
  url.searchParams.set('state', state);
  url.searchParams.set('access_type', 'offline');
  url.searchParams.set('prompt', 'consent');
  url.searchParams.set('include_granted_scopes', 'true');
  window.location.href = url.toString();
  return null;
}
